import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onOpenContactForm }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'Process', id: 'process' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Benefits', id: 'benefits' },
    { label: 'Reviews', id: 'testimonials' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <header data-testid="main-header" className="fixed top-0 left-0 right-0 z-50">
      {/* Top urgency strip */}
      <div className="bg-[#FF1010] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between py-1.5 text-xs font-bold tracking-wide">
          <span className="hidden sm:block uppercase">Every Day Without a Trademark Is a Day Your Brand Is at Risk</span>
          <a href="tel:+18001234567" className="flex items-center gap-1.5 text-white hover:text-white/80 transition-colors">
            <Phone className="w-3 h-3" />
            +1 (800) 123-4567
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className={`transition-all duration-200 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-[#E5E7EB] shadow-sm'
          : 'bg-white/80 backdrop-blur-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-3">
          <div className="flex items-center justify-between">
            <button
              data-testid="header-logo"
              onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="cursor-pointer"
            >
              <span className="text-xl md:text-2xl font-black tracking-tighter text-[#111827] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Trademark<span className="text-[#FF1010]">.</span>Agency
              </span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-0">
              {navLinks.map(link => (
                <button
                  key={link.id}
                  data-testid={`nav-${link.id}`}
                  onClick={() => scrollToSection(link.id)}
                  className="px-4 py-2 text-sm font-semibold text-[#4B5563] hover:text-[#111827] uppercase tracking-wide transition-colors"
                >
                  {link.label}
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Button
                data-testid="header-cta-button"
                onClick={onOpenContactForm}
                className="bg-[#FF1010] hover:bg-[#CC0000] text-white font-bold px-6 h-10 text-sm rounded-none uppercase tracking-wider btn-brutal"
              >
                Get Started Now
              </Button>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-[#111827] p-1"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div data-testid="mobile-menu" className="lg:hidden mt-3 pb-4 border-t border-[#E5E7EB] pt-4">
              <nav className="flex flex-col gap-1">
                {navLinks.map(link => (
                  <button
                    key={link.id}
                    data-testid={`mobile-nav-${link.id}`}
                    onClick={() => scrollToSection(link.id)}
                    className="text-left px-3 py-2.5 text-sm font-semibold text-[#4B5563] hover:text-[#FF1010] hover:bg-[#FF1010]/5 uppercase tracking-wide transition-colors"
                  >
                    {link.label}
                  </button>
                ))}
                <div className="pt-3 mt-2 border-t border-[#E5E7EB]">
                  <a href="tel:+18001234567" className="flex items-center gap-2 text-sm text-[#4B5563] mb-3 px-3 font-medium">
                    <Phone className="w-4 h-4 text-[#FF1010]" /> +1 (800) 123-4567
                  </a>
                  <Button
                    data-testid="mobile-cta-button"
                    onClick={onOpenContactForm}
                    className="w-full bg-[#FF1010] hover:bg-[#CC0000] text-white font-bold h-11 rounded-none uppercase tracking-wider"
                  >
                    Get Started Now
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
